import React, { useCallback, useEffect, useState } from "react";
import { EmotionChip } from "./EmotionChip";
import axios from "axios";
import { AlertTriangle, BookOpenText, ChevronDown, RefreshCw } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Skeleton } from "./ui/skeleton";
import { StaleIndicator } from "./StaleIndicator";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatSceneRange = (item) => (
  item.introduced_in_scene === item.last_seen_in_scene
    ? `scene ${item.introduced_in_scene}`
    : `scenes ${item.introduced_in_scene}-${item.last_seen_in_scene}`
);

const formatStateLabel = (key) => key.replace(/_/g, " ");

const formatStateValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not recorded yet.";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.length ? value.map((item) => formatStateValue(item)).join(", ") : "Not recorded yet.";
  }

  if (typeof value === "object") {
    const nestedEntries = Object.entries(value)
      .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined && nestedValue !== "")
      .map(([nestedKey, nestedValue]) => `${formatStateLabel(nestedKey)}: ${formatStateValue(nestedValue)}`);

    return nestedEntries.length ? nestedEntries.join(" • ") : "Not recorded yet.";
  }

  return String(value);
};

const getCharacterStateEntries = (state) => {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return [{ label: "state", value: formatStateValue(state) }];
  }

  const entries = Object.entries(state)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => ({ label: formatStateLabel(key), value: formatStateValue(value) }));

  return entries.length ? entries : [{ label: "state", value: "Not recorded yet." }];
};

export const ContinuityPanel = ({ episodeId, sceneNumber }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [continuity, setContinuity] = useState(null);

  const loadContinuity = useCallback(async () => {
    if (!episodeId || !sceneNumber) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/episodes/${episodeId}/continuity`, {
        params: { scene_number: sceneNumber },
        withCredentials: true,
      });
      setContinuity(response.data);
    } catch (err) {
      console.error(err);
      setError("Could not load scene context");
    } finally {
      setLoading(false);
    }
  }, [episodeId, sceneNumber]);

  useEffect(() => {
    loadContinuity();
  }, [loadContinuity]);

  return (
    <Card
      className="border-border-subtle bg-surface-card shadow-none"
      data-testid="continuity-panel"
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-sm font-bold" data-testid="continuity-panel-title">
                <BookOpenText className="h-4 w-4 text-[var(--accent-gold)]" />
                Scene Context
              </CardTitle>
              <p className="mt-1 text-xs text-[var(--ink-tertiary)]" data-testid="continuity-panel-subtitle">
                What earlier scenes established before Scene {sceneNumber || "—"}
              </p>
            </div>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={isOpen ? "Collapse scene context panel" : "Expand scene context panel"}
                className="h-8 w-8 rounded-full border border-[var(--border-subtle)] bg-surface-sunk text-[var(--ink-tertiary)] hover:bg-surface-hover hover:text-ink-primary"
                data-testid="continuity-panel-toggle"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`} />
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="space-y-4 px-4 pb-4 pt-0">
            {loading ? (
              <div className="space-y-3" data-testid="continuity-panel-loading">
                <Skeleton className="h-16 w-full rounded-xl bg-[var(--surface-hover)]" />
                <Skeleton className="h-24 w-full rounded-xl bg-[var(--surface-hover)]" />
                <Skeleton className="h-20 w-full rounded-xl bg-[var(--surface-hover)]" />
              </div>
            ) : error ? (
              <Alert className="border-red-500/30 bg-red-500/10 text-red-100" data-testid="continuity-panel-error">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Could not load scene context</AlertTitle>
                <AlertDescription className="mt-2 flex items-center justify-between gap-3">
                  <span>Try again to refresh the continuity summary for this scene.</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={loadContinuity}
                    className="border-red-400/40 bg-transparent text-red-100 hover:bg-red-500/10 hover:text-[var(--ink-primary)]"
                    data-testid="continuity-panel-retry-button"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            ) : continuity?.previous_scenes?.length === 0 ? (
              <div
                className="rounded-xl border border-dashed border-border-subtle bg-surface-sunk px-4 py-5 text-sm text-[var(--ink-secondary)]"
                data-testid="continuity-panel-empty-state"
              >
                This is the first scene — no prior context yet. You're starting fresh!
              </div>
            ) : (
              <>
                {continuity?.stale_count > 0 && (
                  <Alert
                    className="border-amber-400/30 bg-amber-500/10 text-amber-100"
                    data-testid="continuity-stale-warning-banner"
                  >
                    <AlertTriangle className="h-4 w-4 text-[var(--semantic-warning)]" />
                    <AlertTitle>Continuity warning</AlertTitle>
                    <AlertDescription data-testid="continuity-stale-warning-text">
                      ⚠ {continuity.stale_count} scene(s) have outdated context. Re-forge to refresh.
                    </AlertDescription>
                  </Alert>
                )}

                <section data-testid="continuity-previous-scenes-section">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink-tertiary)]">Previous Scenes</h3>
                    <Badge variant="outline" className="border-border-subtle bg-surface-sunk text-ink-secondary" data-testid="continuity-previous-scenes-count">
                      {continuity.previous_scenes.length} linked
                    </Badge>
                  </div>
                  <Accordion
  type="single"
  collapsible
  defaultValue={
    continuity.previous_scenes.length
      ? `scene-${continuity.previous_scenes[continuity.previous_scenes.length - 1].scene_number}`
      : undefined
  }
  className="rounded-xl border border-border-subtle bg-surface-sunk px-3"
>
                    {continuity.previous_scenes.map((previousScene) => (
                      <AccordionItem
                        key={previousScene.scene_number}
                        value={`scene-${previousScene.scene_number}`}
                        className="border-[var(--border-subtle)]"
                        data-testid={`continuity-scene-item-${previousScene.scene_number}`}
                      >
                        <AccordionTrigger
                          className="gap-3 py-3 hover:no-underline"
                          data-testid={`continuity-scene-trigger-${previousScene.scene_number}`}
                        >
                          <div className="min-w-0 flex-1 text-left">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-mono text-[var(--ink-tertiary)]" data-testid={`continuity-scene-number-${previousScene.scene_number}`}>
                                Scene {previousScene.scene_number}
                              </span>
                              <span className="truncate text-sm font-semibold text-[var(--ink-primary)]" data-testid={`continuity-scene-title-${previousScene.scene_number}`}>
                                {previousScene.title || `Scene ${previousScene.scene_number}`}
                              </span>
                              <StaleIndicator
                                stale={previousScene.stale}
                                size="sm"
                                data-testid={`continuity-scene-stale-${previousScene.scene_number}`}
                              />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {previousScene.primary_ce && (
                                <div className="flex items-center gap-[6px]">
                                  <EmotionChip ceCode={previousScene.primary_ce} size="sm" data-testid={`continuity-scene-ce-${previousScene.scene_number}`} />
                                  {previousScene.secondary_ce && <EmotionChip ceCode={previousScene.secondary_ce} size="sm" data-testid={`continuity-scene-secondary-ce-${previousScene.scene_number}`} />}
                                </div>
                              )}
                              {previousScene.scene_type && (
                                <span
                                  className="text-xs text-[var(--ink-tertiary)]"
                                  data-testid={`continuity-scene-type-${previousScene.scene_number}`}
                                >
                                  {previousScene.scene_type}
                                </span>
                              )}
                            </div>
                            <p
                              className="mt-2 line-clamp-2 text-xs leading-relaxed text-[var(--ink-secondary)]"
                              data-testid={`continuity-scene-summary-preview-${previousScene.scene_number}`}
                            >
                              {previousScene.emotional_summary || "No emotional summary recorded yet."}
                            </p>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p
                            className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-hover)] px-3 py-3 text-xs leading-relaxed text-[var(--ink-secondary)]"
                            data-testid={`continuity-scene-summary-full-${previousScene.scene_number}`}
                          >
                            {previousScene.emotional_summary || "No emotional summary recorded yet."}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>

                <section className="space-y-2" data-testid="continuity-objects-section">
  <Collapsible defaultOpen={true}>
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink-tertiary)]">Objects in Play</h3>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[var(--ink-tertiary)] hover:bg-surface-hover">
          <ChevronDown className="h-3.5 w-3.5 transition-transform [[data-state=open]>&]:rotate-180" />
        </Button>
      </CollapsibleTrigger>
    </div>
    <CollapsibleContent className="mt-2">
      {continuity?.objects?.length ? (
        <div className="space-y-2 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunk)] p-3">
          {continuity.objects.map((item, index) => (
            <div
              key={`${item.name}-${item.introduced_in_scene}-${index}`}
              className="flex items-center justify-between gap-3 rounded-lg bg-[var(--surface-hover)] px-3 py-2"
              data-testid={`continuity-object-${index + 1}`}
            >
              <span className="text-sm text-[var(--ink-primary)]">{item.name}</span>
              <span className="text-xs text-[var(--ink-tertiary)]">{formatSceneRange(item)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[var(--ink-tertiary)]" data-testid="continuity-objects-empty">No tracked objects yet.</p>
      )}
    </CollapsibleContent>
  </Collapsible>
</section>

                <section className="space-y-2" data-testid="continuity-characters-section">
  <Collapsible defaultOpen={true}>
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink-tertiary)]">Character States</h3>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[var(--ink-tertiary)] hover:bg-surface-hover">
          <ChevronDown className="h-3.5 w-3.5 transition-transform [[data-state=open]>&]:rotate-180" />
        </Button>
      </CollapsibleTrigger>
    </div>
    <CollapsibleContent className="mt-2">
      {Object.entries(continuity?.characters || {}).length ? (
        <div className="space-y-2 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunk)] p-3">
          {Object.entries(continuity.characters).map(([name, details], index) => {
            const stateEntries = getCharacterStateEntries(details.last_state);

            return (
              <div
                key={name}
                className="rounded-lg bg-[var(--surface-hover)] px-3 py-2"
                data-testid={`continuity-character-${index + 1}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-[var(--ink-primary)]" data-testid={`continuity-character-name-${index + 1}`}>
                    {name}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]" data-testid={`continuity-character-scene-${index + 1}`}>
                    Scene {details.last_updated_scene}
                  </span>
                </div>
                <div className="mt-2 space-y-2" data-testid={`continuity-character-state-list-${index + 1}`}>
                  {stateEntries.map((entry, stateIndex) => (
                    <div
                      key={`${name}-${entry.label}-${stateIndex}`}
                      className="space-y-1 rounded-md border border-[var(--border-subtle)] bg-[var(--surface-sunk)] px-2.5 py-2"
                      data-testid={`continuity-character-state-${index + 1}-${stateIndex + 1}`}
                    >
                      <p
                        className="text-[11px] uppercase tracking-[0.16em] text-[var(--ink-tertiary)]"
                        data-testid={`continuity-character-state-label-${index + 1}-${stateIndex + 1}`}
                      >
                        {entry.label}
                      </p>
                      <p
                        className="text-xs leading-relaxed text-[var(--ink-secondary)]"
                        data-testid={`continuity-character-state-value-${index + 1}-${stateIndex + 1}`}
                      >
                        {entry.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[var(--ink-tertiary)]" data-testid="continuity-characters-empty">No tracked character states yet.</p>
      )}
    </CollapsibleContent>
  </Collapsible>
</section>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};